// ---------------------------------------------------------------------------
// seo-product.zh.ts — Chinese translations for 6 SEO product landing pages
// ---------------------------------------------------------------------------
import type { SEOProductText } from './seo-product.content';

export const SEO_PRODUCT_ZH: Record<string, SEOProductText> = {
  'hotel-management-software': {
    heroTitle: '酒店管理软件 — 全面管理您的物业',
    heroSubtitle: '从一个云平台自动化前台、预订、客房管理、账单和客人沟通，搭载25项AI功能，包括动态定价、AI礼宾和预测性运营。超过40个国家的2,500多家酒店信赖之选。',
    heroCtaText: '免费试用',
    problemTitle: '没有现代软件的酒店面临的挑战',
    problemPoints: ['手动入住/退房每天浪费员工数小时', 'OTA渠道未连接导致超售损害声誉', '未追踪费用和账单错误造成收入流失', '无法了解入住率趋势或RevPAR表现', '服务缓慢和沟通不畅导致客人投诉', '地区税务报告和客人登记的合规问题'],
    features: [
      { title: '前台与预订', description: '实时房态板、拖拽入住、散客预订和团体预订 — 全部在一个仪表板中。' },
      { title: '渠道管理器', description: '与Booking.com、Expedia、Airbnb、Agoda等100+OTA双向同步。GDS连接和房价对等监控。' },
      { title: '收益智能', description: '实时RevPAR、ADR和入住率分析。动态定价规则和假设收入模拟器。' },
      { title: '客户CRM', description: '包含偏好、沟通历史、忠诚度积分和消费分析的360度客户档案。' },
      { title: '账单与支付', description: '自动开票、分账、多币种支持、地区税务合规和Stripe/PayPal集成。' },
      { title: '合规与安全', description: '地区税务自动化、客人登记、GDPR合规、审计追踪、基于角色的访问控制和AES-256加密。' },
      { title: '25项AI功能', description: 'AI动态定价、客户礼宾聊天机器人、智能客房管理排程、夜审异常检测、情感分析、需求预测，以及19项更多AI功能集成于每个模块。' },
    ],
    benefitsTitle: 'HotelStack.io的验证成果',
    benefits: [
      { label: '减少手动工作', description: '自动化处理日常任务，让您的团队专注于客人服务' },
      { label: '更多直接预订', description: '集成预订引擎消除OTA佣金' },
      { label: '入住率提升', description: '收益智能优化房价以实现最高入住率' },
      { label: '平均响应时间', description: '24/7专属支持，响应时间低于2小时' },
      { label: '入驻时间', description: '48小时内免费数据迁移和设置' },
      { label: '可用性SLA', description: '多区域冗余的企业级可靠性' },
    ],
    testimonial: { quote: '自从转用HotelStack.io以来，我们的入住率上升了23%，手动错误几乎降为零。员工喜爱其直观的操作体验。', role: '总经理', hotel: 'The Greenwich Inn，伦敦' },
    faqItems: [
      { question: '酒店管理软件包含什么？', answer: 'HotelStack.io包含PMS、渠道管理器、预订引擎、客户CRM、收益智能、账单、客房管理、合规工具和多物业管理 — 全部集于一个平台。' },
      { question: '适合小型酒店吗？', answer: '是的。我们的Starter计划每月49美元，专为50间客房以下的物业设计，包含所有核心功能。提供区域定价。' },
      { question: '设置需要多长时间？', answer: '大多数酒店在48小时内完全运营。我们的团队免费处理数据迁移、员工培训和渠道配置。' },
      { question: '购买前可以试用吗？', answer: '当然可以。每个计划包含14天免费试用，完全功能访问。无需信用卡。' },
    ],
    ctaTitle: '准备好现代化您的酒店运营了吗？',
    ctaSubtitle: '加入40多个国家超过2,500家已经使用HotelStack.io自动化运营和提升收入的酒店。',
    relatedPages: [
      { label: '酒店PMS软件', description: '物业管理系统功能详解' },
      { label: '渠道管理器', description: 'OTA同步如何消除超售' },
      { label: '收益管理', description: '动态定价和RevPAR优化' },
      { label: '预订引擎', description: '直接预订和消除OTA佣金' },
      { label: '客户CRM', description: '360度档案和忠诚度管理' },
    ],
  },
  'hotel-pms-software': {
    heroTitle: '酒店PMS软件 — 云端物业管理系统',
    heroSubtitle: '面向酒店的AI驱动云端物业管理系统。动态定价、智能客房管理、夜审异常检测和AI驱动洞察。从任何设备管理客房、预订和入住。',
    heroCtaText: '免费试用PMS',
    problemTitle: '传统PMS系统为何阻碍酒店发展',
    problemPoints: ['本地服务器需要昂贵的IT维护', '无移动端访问 — 员工被绑定在前台终端', '缓慢的入住流程让客人不满并造成排队', '系统不连通意味着数据重复录入', '无实时报表 — 基于过时数据做决策', '缺乏与现代支付网关的集成'],
    features: [
      { title: '可视化房态板', description: '支持实时状态的拖拽房间分配。入住、清洁和维护的颜色编码指示。' },
      { title: '预订管理', description: '带可用性网格的预订日历。从一个界面管理直接、OTA、散客和团体预订。' },
      { title: '客房管理仪表板', description: '任务分配、检查清单、布草追踪和实时房间状态更新。' },
      { title: '夜审向导', description: '带预审检查、收入对账和审计摘要报告的自动日结。' },
      { title: '客户账单管理', description: '统一客户账单，含费用记录、分账、押金追踪和公司结算工作流。' },
      { title: '实时同步', description: '每次更改即时同步到所有设备。无需刷新 — 多名员工可同时工作。' },
      { title: 'AI驱动运营', description: '动态定价引擎、智能客房管理优先排程、夜审异常检测和每个PMS模块的AI驱动洞察。' },
    ],
    benefitsTitle: '我们的PMS带来的价值',
    benefits: [
      { label: '平均入住时间', description: '预填客户资料和电子签名的快速入住' },
      { label: '减少纸质工作', description: '电子账单、电子发票和自动报表取代手动记录' },
      { label: '云端访问', description: '从任何浏览器访问PMS — 桌面、平板或手机' },
      { label: '实时可见性', description: '入住率、收入和运营KPI的实时仪表板' },
    ],
    testimonial: { quote: 'PMS仪表板彻底改变了我们的运营方式。入住从8分钟缩短到3分钟以内，夜审现在只需10分钟。', role: '收益总监', hotel: 'Marina Bay Suites，迪拜' },
    faqItems: [
      { question: '什么是酒店PMS？', answer: '物业管理系统（PMS）是酒店的核心运营软件。它处理客房管理、预订、入住/退房、账单、客房管理和报表。HotelStack.io的PMS还集成了渠道管理、收益工具和客户CRM。' },
      { question: 'HotelStack.io的PMS是云端的吗？', answer: '是的。我们的PMS完全在云端运行，无需安装软件。从任何浏览器访问，99.9%正常运行时间保证。' },
      { question: '可以从当前PMS迁移吗？', answer: '可以。我们的团队处理从任何PMS的完整数据迁移。免费迁移通常在48小时内完成。' },
      { question: '支持多物业连锁吗？', answer: '当然支持。我们的连锁管理模块提供跨物业报表、价格管理和员工监督的集中控制。' },
    ],
    ctaTitle: '升级到现代云端PMS',
    ctaSubtitle: '最快的云端酒店PMS。14天免费试用，零设置费用，免费迁移。',
    relatedPages: [
      { label: '酒店管理软件', description: '一体化酒店运营平台' },
      { label: '渠道管理器', description: '自动同步库存到100+OTA' },
      { label: '预订引擎', description: '零佣金接受直接预订' },
    ],
  },
  'hotel-booking-engine': {
    heroTitle: '酒店预订引擎 — 推动直接预订',
    heroSubtitle: '通过BookMyRoom.io的AI驱动预订消除OTA佣金。智能追加销售推荐、AI礼宾、实时可用性、即时确认和无缝PMS集成。',
    heroCtaText: '获取预订引擎',
    problemTitle: 'OTA依赖的代价',
    problemPoints: ['OTA佣金消耗每笔预订收入的15-25%', '与客人无直接关系 — OTA拥有客户', '价格对等限制了定价灵活性', 'OTA结算流程导致付款周期延迟'],
    features: [
      { title: 'BookMyRoom.io展示', description: '每个物业在BookMyRoom.io获得SEO优化的展示页，包含照片、设施、评价和房价。' },
      { title: '即时确认', description: '实时可用性检查和即时预订确认。预订自动同步到PMS。' },
      { title: '客人评价', description: '经过验证的评价建立信任并提高转化率。包含评价管理工具。' },
      { title: '安全支付', description: '通过Stripe和PayPal的集成支付处理，支持多币种、自动收据和退款管理。' },
      { title: '转化分析', description: '追踪预订漏斗、转化率、热门页面和按来源的收入归因。' },
      { title: '客人沟通', description: '通过邮件和短信发送自动预订确认、入住前邮件和入住提醒。' },
      { title: 'AI礼宾与追加销售', description: 'AI聊天机器人在预订过程中协助客人，提供客房推荐、套餐建议和基于客户档案与偏好的个性化追加销售。' },
    ],
    benefitsTitle: '预订引擎成果',
    benefits: [
      { label: '更多直接预订', description: '物业直接预订增长40%，零佣金' },
      { label: '直接预订佣金', description: '保留BookMyRoom.io预订100%的收入' },
      { label: '数据获取', description: '通过完整的资料访问掌握客户关系' },
    ],
    testimonial: { quote: 'BookMyRoom.io在第一季度为我们带来了40%更多的直接预订。仅佣金节省就支付了整个HotelStack.io订阅费用。', role: '业主', hotel: 'Casa del Sol，巴塞罗那' },
    faqItems: [
      { question: '预订引擎如何工作？', answer: '您的物业在BookMyRoom.io获得展示页，客人可以搜索、查看客房、阅读评价并直接预订。预订即时同步到您的HotelStack.io仪表板。' },
      { question: '直接预订需要支付佣金吗？', answer: '不需要。通过BookMyRoom.io的预订100%免佣金。您只需支付HotelStack.io订阅费。' },
      { question: '可以自定义展示页吗？', answer: '可以。您可以从HotelStack.io仪表板直接控制照片、客房描述、房价、政策和设施。' },
    ],
    ctaTitle: '立即开始接受直接预订',
    ctaSubtitle: '每个HotelStack.io计划都包含BookMyRoom.io展示。零额外费用，零佣金。',
    relatedPages: [
      { label: '渠道管理器', description: '将OTA分销与直接预订并行管理' },
      { label: '酒店管理软件', description: '包含预订引擎的完整平台' },
      { label: '客户CRM', description: '从直接预订数据建立客户关系' },
    ],
  },
  'hotel-channel-manager': {
    heroTitle: '酒店渠道管理器 — 实时同步100+OTA',
    heroSubtitle: '与Booking.com、Expedia、Airbnb、Agoda及100多个渠道的AI驱动房价同步。AI竞争对手房价监控、基于需求的定价、通过Amadeus和Sabre的GDS连接。自动防止超售。',
    heroCtaText: '免费试用渠道管理器',
    problemTitle: '手动渠道管理的风险',
    problemPoints: ['手动更新延迟导致超售', '价格对等违规触发OTA处罚', '每天花费数小时在多个后台上', '无法集中查看各渠道表现'],
    features: [
      { title: '实时同步', description: '所有渠道的房价、可用性和限制条件的即时双向同步。亚秒级更新。' },
      { title: '价格对等监控', description: '所有渠道的自动价格对等检查。检测到差异时发出警报。' },
      { title: '渠道绩效', description: '比较各渠道的收入、预订量和ADR。识别最佳渠道并优化分销。' },
      { title: '同步日志', description: '每次同步操作的完整审计追踪。追踪失败、重试和数据差异。' },
      { title: '收益管理', description: '按渠道设置房价规则、最低住宿天数和关闭日期。按渠道优化分销策略。' },
      { title: '100+渠道', description: '与Booking.com、Expedia、Airbnb、Agoda、TripAdvisor、Hostelworld及全球区域OTA的原生集成。' },
      { title: 'AI驱动分销', description: 'AI竞争对手房价监控实时分析市场定位，基于需求的定价为每个渠道建议最优房价，智能渠道分配在整个分销网络中最大化收入。' },
    ],
    benefitsTitle: '渠道管理器的影响',
    benefits: [
      { label: '超售', description: '实时同步消除所有渠道的重复预订' },
      { label: '已连接OTA', description: '从一个仪表板分销到所有主要旅游平台' },
      { label: '每周节省', description: '消除手动后台更新和房价上传' },
    ],
    testimonial: { quote: '我们过去每天花2小时在OTA后台更新房价。有了HotelStack.io渠道管理器，一切自动化。6个月零超售。', role: '收益经理', hotel: 'Le Marais Hotel，巴黎' },
    faqItems: [
      { question: 'HotelStack.io连接哪些OTA？', answer: '我们支持100多个渠道，包括Booking.com、Expedia、Airbnb、Agoda、TripAdvisor、Hostelworld及全球区域平台。还支持通过Amadeus和Sabre的GDS连接。' },
      { question: '房价同步有多快？', answer: '房价和可用性更新在5秒内同步到所有渠道。我们的实时引擎持续运行，无轮询间隔。' },
      { question: '同步失败怎么办？', answer: '失败的同步会自动重试。您将收到即时警报，所有失败都会记录完整详情。' },
    ],
    ctaTitle: '自动化您的渠道分销',
    ctaSubtitle: '在100+OTA上自动同步房价和可用性。不再超售，不再手动更新。',
    relatedPages: [
      { label: '收益管理', description: '用动态规则优化跨渠道定价' },
      { label: '预订引擎', description: '推动零佣金的直接预订' },
      { label: '酒店管理软件', description: '内置渠道管理器的完整平台' },
    ],
  },
  'hotel-crm': {
    heroTitle: '酒店CRM — 360度客户关系管理',
    heroSubtitle: '通过AI驱动的客户画像、情感分析、个性化推荐、忠诚度计划和自动沟通建立持久的客户关系。将一次性客人转化为忠实常客。',
    heroCtaText: '免费试用CRM',
    problemTitle: '没有CRM为何会丢失客户数据',
    problemPoints: ['住宿间客人偏好被遗忘 — 无法个性化', '无消费历史、沟通和反馈的统一视图', '用电子表格管理忠诚度计划和手动追踪', '缺乏客户智能导致错过追加销售机会'],
    features: [
      { title: '360度档案', description: '包含住宿历史、偏好、消费分析、沟通日志和忠诚度状态的统一客户档案。' },
      { title: '忠诚度计划', description: '可配置的分级忠诚度，包含积分规则引擎、奖励目录和自动等级升级。' },
      { title: '自动沟通', description: '入住前欢迎邮件、生日祝福、反馈请求和再参与活动。' },
      { title: '消费分析', description: '追踪客户终身价值、每次住宿平均消费、偏好房型和餐饮消费模式。' },
      { title: '偏好追踪', description: '记录并记住房间偏好、饮食需求、枕头类型和特殊场合详情。' },
      { title: '直接预订转化', description: '利用CRM数据提供个性化房价和套餐，推动重复直接预订。' },
      { title: 'AI客户智能', description: 'AI驱动的客户画像构建360度行为模型，情感分析监控评论和消息中的满意度，预测性流失检测在客户离开前识别高风险客户。' },
    ],
    benefitsTitle: '酒店CRM成果',
    benefits: [
      { label: '回头客率', description: '使用CRM的酒店回头客预订增加35%' },
      { label: '客户终身价值', description: '个性化体验使平均客户终身价值翻倍' },
      { label: '减少手动跟进', description: '自动沟通处理日常客户接触点' },
    ],
    testimonial: { quote: '在客人到达前了解他们的偏好彻底改变了体验。自从使用CRM模块以来，回头客预订增加了35%。', role: '总经理', hotel: 'Tokyo Azabu Hotel，东京' },
    faqItems: [
      { question: 'CRM包含在所有计划中吗？', answer: '是的。客户CRM包含在每个HotelStack.io计划中。忠诚度计划管理和高级分析在Professional和Enterprise计划中可用。' },
      { question: '可以导入现有客户数据吗？', answer: '可以。我们的团队从任何来源迁移客户数据。免费迁移通常在24小时内完成。' },
      { question: '支持邮件和短信集成吗？', answer: '支持。内置自动邮件和短信。连接您自己的邮件域名或使用我们的托管发送服务。' },
    ],
    ctaTitle: '立即开始建立客户忠诚度',
    ctaSubtitle: '将每次住宿转化为关系。CRM免费包含在每个HotelStack.io计划中。',
    relatedPages: [
      { label: '酒店管理软件', description: '内置CRM的完整平台' },
      { label: '预订引擎', description: '从直接预订中获取客户数据' },
      { label: '收益管理', description: '利用客户数据优化定价' },
    ],
  },
  'hotel-revenue-management': {
    heroTitle: '酒店收益管理软件 — 最大化RevPAR',
    heroSubtitle: 'AI驱动的动态定价、ML需求预测、AI竞争对手房价监控和收入模拟器。通过AI驱动的定价决策将RevPAR最大化18-25%。',
    heroCtaText: '免费试用收益工具',
    problemTitle: '没有智能定价的收益风险',
    problemPoints: ['固定房价在高需求期间错失收入', '无法了解竞争对手定价以进行战略定位', '没有需求数据支持的凭直觉定价', '无法预测季节性趋势和规划房价策略'],
    features: [
      { title: '动态定价规则', description: '根据入住率阈值、星期几、季节和需求信号自动调整房价的规则。' },
      { title: '需求预测', description: '使用历史数据、本地事件和市场趋势的ML预测来预测入住率。' },
      { title: '竞争对手房价监控', description: '实时追踪OTA上的竞争对手定价。竞争对手变更房价时自动警报。' },
      { title: 'RevPAR分析', description: '实时RevPAR、ADR和入住率仪表板，包含趋势分析、基准比较和目标追踪。' },
      { title: '假设模拟器', description: '在执行前模拟收入场景。虚拟测试房价变更、最低住宿天数和套餐优惠。' },
      { title: '收入报表', description: '每日快报、节奏和拾取分析、渠道贡献细分和细分市场表现。' },
      { title: '自主AI定价', description: '由AI驱动的全天候免人工房价管理，根据需求信号、竞争对手动态、市场条件和预订模式实时调整价格——无需人工干预。' },
    ],
    benefitsTitle: '收益管理的影响',
    benefits: [
      { label: 'RevPAR提升', description: '动态定价在需求高峰期捕获最优收入' },
      { label: '入住率增长', description: '智能房价策略在淡季填满客房' },
      { label: '订阅ROI', description: '收入增长通常是平台成本的3倍' },
    ],
    testimonial: { quote: '仅收益分析仪表板就值回整个订阅费。通过动态定价，我们在第一季度将RevPAR提高了18%。', role: '收益总监', hotel: 'Pacific Resort Group，悉尼' },
    faqItems: [
      { question: '什么是RevPAR？', answer: 'Revenue Per Available Room（每可用客房收入）是酒店关键绩效指标 = ADR × 入住率。HotelStack.io实时追踪RevPAR并提供最大化工具。' },
      { question: '支持动态定价吗？', answer: '支持。根据入住率、星期几、季节、提前预订天数和竞争对手房价设置自动规则。规则自动执行，无需手动干预。' },
      { question: '可以追踪竞争对手房价吗？', answer: '可以。我们的监控器追踪主要OTA上的定价。竞争对手变更房价时发送自动警报。' },
    ],
    ctaTitle: '释放您酒店的收入潜力',
    ctaSubtitle: '不要再错失收入。基于数据的收益管理从14天免费试用开始。',
    relatedPages: [
      { label: '渠道管理器', description: '将优化后的房价分发到100+渠道' },
      { label: '酒店管理软件', description: '内置收益工具的完整平台' },
      { label: '酒店PMS', description: '集成分析的物业管理' },
    ],
  },
};
